import { useState } from 'react'
import AtmosphericHero from '@/components/marketing/AtmosphericHero'
import ScrollReveal from '@/components/marketing/ScrollReveal'
import type { SharedMarketingProps } from '@/types/marketing'

/**
 * THE TILE IS THE INSTRUCTION.
 *
 * The usual brand page previews every asset on one neutral surface and puts the
 * rule in a caption underneath, which is why the reversed logo keeps turning up
 * on white. Here each asset is shown on the surface it is actually for, so the
 * choice is made by looking rather than by reading.
 */
type Surface = 'light' | 'dark'

interface AssetFile {
  url: string
  /** What the file should be called once it is in someone else's press kit. */
  download: string
}

interface Asset {
  id: string
  name: string
  surface: Surface
  usage: string
  /** What the page RENDERS — an SVG, or a 480px derivative for the raster
   *  tiles. Not the same as what the download buttons hand over: previewing the
   *  full-resolution tiles would pull 1.4MB to draw four thumbnails. */
  preview: string
  svg: AssetFile | null
  png: AssetFile
}

interface Swatch {
  hex: string
  name: string
  role: string
}

interface Misuse {
  id: string
  label: string
  reason: string
}

interface BrandPageProps extends SharedMarketingProps {
  lockups: Asset[]
  icons: Asset[]
  palette: Swatch[]
  misuse: Misuse[]
  /** The trademark line. Server-supplied so the entity name is written once. */
  trademark: string
  owner: string
}

/** Download buttons. `download` is what makes the browser save the clean name
 *  rather than Propshaft's fingerprinted one. */
function DownloadRow({ asset }: { asset: Asset }) {
  return (
    <div className="mt-5 flex flex-wrap gap-2">
      {asset.svg && (
        <a className="brand-download" href={asset.svg.url} download={asset.svg.download}>
          SVG
          <span className="brand-download-hint">vector</span>
        </a>
      )}
      <a className="brand-download" href={asset.png.url} download={asset.png.download}>
        PNG
        <span className="brand-download-hint">raster</span>
      </a>
      {!asset.svg && (
        <span className="brand-download-none">
          Raster only — this artwork has a baked background.
        </span>
      )}
    </div>
  )
}

function AssetCard({ asset }: { asset: Asset }) {
  return (
    <figure className="brand-asset">
      <div className={`brand-stage brand-stage-${asset.surface}`}>
        <img
          src={asset.preview}
          alt={`wavbee ${asset.name.toLowerCase()}, shown on a ${asset.surface} background`}
          className="brand-stage-art"
          loading="lazy"
        />
        <span className="brand-stage-tag">
          {asset.surface === 'light' ? 'On light' : 'On dark'}
        </span>
      </div>
      <figcaption className="brand-asset-body">
        <h3 className="brand-asset-name">{asset.name}</h3>
        <p className="brand-asset-usage">{asset.usage}</p>
        <DownloadRow asset={asset} />
      </figcaption>
    </figure>
  )
}

/** Click the swatch, get the hex. The whole tile is the button because a
 *  4px "copy" icon is a worse target than the colour itself. */
function ColorSwatch({ swatch }: { swatch: Swatch }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(swatch.hex)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1600)
    } catch {
      // Clipboard is blocked (insecure origin, or the user said no). The hex is
      // on screen and selectable, so say nothing rather than throw a toast at
      // someone who can simply read it.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      className="brand-swatch"
      aria-label={`Copy ${swatch.name} ${swatch.hex}`}
    >
      <span className="brand-swatch-chip" style={{ background: swatch.hex }} aria-hidden="true" />
      <span className="brand-swatch-meta">
        <span className="brand-swatch-name">{swatch.name}</span>
        <span className="brand-swatch-hex">{swatch.hex}</span>
        <span className="brand-swatch-role">{swatch.role}</span>
      </span>
      <span className="brand-swatch-action" aria-hidden="true">{copied ? 'Copied' : 'Copy'}</span>
      <span className="sr-only" role="status">{copied ? `${swatch.hex} copied` : ''}</span>
    </button>
  )
}

/** The mistakes, rendered. Every guideline writes "do not stretch the logo" and
 *  every logo gets stretched, because a sentence is easy to skim and a picture
 *  of the error is not. `data-misuse` drives the distortion in CSS. */
function MisuseTile({ item, logo }: { item: Misuse; logo: string }) {
  return (
    <div className="brand-misuse" data-misuse={item.id}>
      <div className="brand-misuse-stage">
        <img src={logo} alt="" aria-hidden="true" className="brand-misuse-art" loading="lazy" />
      </div>
      <p className="brand-misuse-label">
        <span className="brand-misuse-cross" aria-hidden="true">✕</span>
        {item.label}
      </p>
      <p className="brand-misuse-reason">{item.reason}</p>
    </div>
  )
}

export default function Brand(props: BrandPageProps) {
  const primary = props.lockups.find(l => l.surface === 'light') ?? props.lockups[0]
  const reversed = props.lockups.find(l => l.surface === 'dark') ?? props.lockups[0]
  const primaryArt = primary.preview
  const reversedArt = reversed.preview

  return (
    <>
      {/* The hero shows the reversed lockup on a dark surface — the page's own
          rule, applied to itself, before it is stated. */}
      <AtmosphericHero minHeight="58vh" containerClassName="hero-content">
        <div className="hero-text">
          <span className="hero-eyebrow">Brand</span>
          <h1 className="hero-headline" style={{ fontSize: 'clamp(2.4rem, 6.5vw, 5.2rem)' }}>
            The wavbee logo, and how to use it.
          </h1>
          <p className="hero-body">
            Everything here is free to use when you write about wavbee, link to us, or build on the
            platform. Take the vector wherever you can — it stays sharp at any size.
          </p>
          <div className="brand-hero-mark">
            <img src={reversedArt} alt="wavbee" className="brand-hero-art" />
          </div>
        </div>
      </AtmosphericHero>

      {/* ── Lockups ─────────────────────────────────────────────── */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">The logo</div>
              <h2 className="marketing-section-title marketing-section-title-dark">
                Two versions. Pick the one that matches your background.
              </h2>
              <p className="brand-section-lede">
                Same artwork both times — only the wordmark changes colour so it stays readable.
                Each is shown below on the surface it belongs on.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid gap-6 md:grid-cols-2">
            {props.lockups.map((asset, i) => (
              <ScrollReveal key={asset.id} direction="up" delay={i * 0.08}>
                <AssetCard asset={asset} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Icons ───────────────────────────────────────────────── */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">The mark</div>
              <h2 className="marketing-section-title">For avatars, app icons and tight spaces.</h2>
              <p className="brand-section-lede brand-section-lede-light">
                Use the mark on its own only when the full lockup would be too small to read —
                roughly below 120&nbsp;px wide.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid gap-6 sm:grid-cols-2 xl:grid-cols-4">
            {props.icons.map((asset, i) => (
              <ScrollReveal key={asset.id} direction="up" delay={i * 0.06}>
                <AssetCard asset={asset} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Clear space, minimum size, misuse ───────────────────── */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Using it well</div>
              <h2 className="marketing-section-title marketing-section-title-dark">
                Give it room, keep it level, leave the colours alone.
              </h2>
            </div>
          </ScrollReveal>

          <div className="grid gap-6 lg:grid-cols-2">
            <ScrollReveal direction="up">
              <div className="marketing-light-card h-full">
                <h3 className="brand-rule-title">Clear space</h3>
                <p className="brand-rule-body">
                  Keep clear space around the logo equal to the height of the mark. Nothing else
                  goes inside the dashed line — not text, not another logo, not the edge of a photo.
                </p>
                <div className="brand-clearspace" aria-hidden="true">
                  <div className="brand-clearspace-inner">
                    <img src={primaryArt} alt="" className="brand-clearspace-art" loading="lazy" />
                  </div>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal direction="up" delay={0.08}>
              <div className="marketing-light-card h-full">
                <h3 className="brand-rule-title">Minimum size</h3>
                <p className="brand-rule-body">
                  Below these widths the mark's strokes start to fill in and the wordmark stops
                  being legible. If you need it smaller than the lockup allows, use the mark.
                </p>
                <dl className="brand-minsize">
                  <div className="brand-minsize-row">
                    <dt>Lockup, screen</dt>
                    <dd>120 px wide</dd>
                  </div>
                  <div className="brand-minsize-row">
                    <dt>Lockup, print</dt>
                    <dd>25 mm wide</dd>
                  </div>
                  <div className="brand-minsize-row">
                    <dt>Mark, screen</dt>
                    <dd>24 px square</dd>
                  </div>
                </dl>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal direction="up">
            <h3 className="brand-misuse-heading">What not to do</h3>
          </ScrollReveal>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {props.misuse.map((item, i) => (
              <ScrollReveal key={item.id} direction="up" delay={i * 0.05}>
                <MisuseTile item={item} logo={primaryArt} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Palette ─────────────────────────────────────────────── */}
      <section className="marketing-section">
        <div className="container mx-auto px-6">
          <ScrollReveal direction="up">
            <div className="marketing-section-header">
              <div className="marketing-eyebrow-mono">Colour</div>
              <h2 className="marketing-section-title">Six values. Click any one to copy it.</h2>
              <p className="brand-section-lede brand-section-lede-light">
                The orange gradient runs amber to red, in that order. Use the greys for type, not
                the oranges.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {props.palette.map((swatch, i) => (
              <ScrollReveal key={swatch.hex} direction="up" delay={i * 0.04}>
                <ColorSwatch swatch={swatch} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Terms ───────────────────────────────────────────────── */}
      <section className="marketing-section marketing-section-warm">
        <div className="container mx-auto px-6">
          <div className="grid gap-6 lg:grid-cols-2">
            <ScrollReveal direction="up">
              <div className="marketing-light-card h-full">
                <h3 className="brand-rule-title">You can</h3>
                <ul className="brand-list">
                  <li>Use the logo to link to wavbee, or to say you distribute through us.</li>
                  <li>Use it in articles, reviews and presentations about wavbee.</li>
                  <li>Scale it, as long as both axes scale together.</li>
                </ul>
              </div>
            </ScrollReveal>
            <ScrollReveal direction="up" delay={0.08}>
              <div className="marketing-light-card h-full">
                <h3 className="brand-rule-title">You can't</h3>
                <ul className="brand-list">
                  <li>Use the logo as your own, or as part of your own logo or product name.</li>
                  <li>Suggest wavbee endorses, sponsors or partners with you when it doesn't.</li>
                  <li>Change the artwork — colours, proportions, spacing or wording.</li>
                </ul>
              </div>
            </ScrollReveal>
          </div>

          <ScrollReveal direction="up">
            <p className="brand-contact">
              Need a format that isn't here, or not sure whether your use is fine?{' '}
              <a href={`mailto:${props.layout.support_email}`} className="brand-contact-link">
                {props.layout.support_email}
              </a>
            </p>
          </ScrollReveal>

          {/* The notice that has to travel with the artwork. Marked up as a
              <small> inside a footer role so it reads as the legal line it is
              rather than as more body copy. */}
          <ScrollReveal direction="up">
            <div className="brand-legal">
              <p className="brand-legal-mark">{props.trademark}</p>
              <p className="brand-legal-note">
                All logos, marks and artwork on this page remain the property of {props.owner}.
                Downloading them does not transfer any right in them beyond the uses described
                above. Read more <a href="/about" className="brand-contact-link">about wavbee</a>,
                or see our <a href="/user-agreement" className="brand-contact-link">terms of service</a>.
              </p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  )
}
